<template>
    <div class="progress-header">
        <span class="icon">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path fill-rule="evenodd" clip-rule="evenodd"
                    d="M8.2002 2.0002C8.2002 1.55837 8.55837 1.2002 9.0002 1.2002H15.0002C15.442 1.2002 15.8002 1.55837 15.8002 2.0002C15.8002 2.44202 15.442 2.8002 15.0002 2.8002H9.0002C8.55837 2.8002 8.2002 2.44202 8.2002 2.0002ZM3.2002 14.0002C3.2002 9.14009 7.14009 5.2002 12.0002 5.2002C16.8603 5.2002 20.8002 9.14009 20.8002 14.0002C20.8002 18.8603 16.8603 22.8002 12.0002 22.8002C7.14009 22.8002 3.2002 18.8603 3.2002 14.0002ZM12.0002 6.8002C8.02374 6.8002 4.8002 10.0237 4.8002 14.0002C4.8002 17.9767 8.02374 21.2002 12.0002 21.2002C15.9767 21.2002 19.2002 17.9767 19.2002 14.0002C19.2002 10.0237 15.9767 6.8002 12.0002 6.8002ZM12.0002 9.2002C12.442 9.2002 12.8002 9.55837 12.8002 10.0002V14.0002C12.8002 14.442 12.442 14.8002 12.0002 14.8002C11.5584 14.8002 11.2002 14.442 11.2002 14.0002V10.0002C11.2002 9.55837 11.5584 9.2002 12.0002 9.2002Z"
                    fill="" />
            </svg>
        </span>
        <span class="label-xl content-1">[CONSTRUCTION TIME: ({{ remainingTime }}s)] <template v-if="null !== syncElementsRemaining"> &#9733;{{ syncElementsRemaining }}</template></span>
    </div>
    <div v-if="showProgressBar" class="progress-bar">
        <div v-for="(slot, index) in totalSlots" :key="index" :class="['slot', { filled: index < filledSlots }]"></div>
    </div>
</template>

<style scoped>
.progress-header {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 12px 0px;
    width: 100%;
}

.progress-bar {
    display: flex;
    gap: 2px;
    border: 1px solid var(--border-1);
    padding: 4px;
    border-radius: var(--border-radius-xxs);
    width: 100%;
}

.slot {
    width: 5%;
    height: 24px;
    background-color: var(--background-4);
    transition: background-color 0.3s ease;
}

.filled {
    background-color: var(--background-3);
}
</style>

<script>
export default {
    name: 'ProgressBarComponent',
    props: {
        totalTime: {
            type: Number,
            required: true,
        },
        elapsedTime: {
            type: Number,
            default: 0
        },
        totalSlots: {
            type: Number,
            default: 24,
        },
        showProgressBar: {
            type: Boolean,
            default: true,
        },
        elementsRemaining: {
            type: [Number, null],
            default: null
        },
        callbackEvent: {
            type: Function,
            default: () => {}
        },
    },
    data() {
        return {
            localElapsedTime: this.elapsedTime,
            worker: null
        };
    },
    computed: {
        remainingTime() {
            return this.totalTime - this.localElapsedTime;
        },
        filledSlots() {
            return Math.floor((this.localElapsedTime / this.totalTime) * this.totalSlots);
        },
        syncElementsRemaining() {
            return this.elementsRemaining;
        }
    },
    mounted() {
        if (this.showProgressBar) {
            this.startProgress();
        }
    },
    beforeUnmount() {
        this.clear();
    },
    watch: {
        showProgressBar(newVal) {
            if (newVal) {
                this.startProgress();
            } else {
                this.clear();
            }
        },
        totalTime(newValue) {
            this.updateWorker({ finalTime: newValue, elapsedTime: this.localElapsedTime });
        },
        elapsedTime(newValue) {
            this.updateWorker({ finalTime: this.totalTime, elapsedTime: newValue });
        }
    },
    methods: {
        startProgress() {
            this.startWorker();
        },
        clear() {
            this.localElapsedTime = 0;
            if (this.worker) {
                this.stopWorker();
            }
        },
        startWorker() {
            if (!this.worker) {
                this.worker = new Worker(new URL('@/web-workers/progressBarWebWorker.js', import.meta.url));
                this.worker.onmessage = (event) => {
                    let finished = event.data.finished;
                    this.localElapsedTime = event.data.elapsedTime;

                    if (finished) {
                        this.clear();
                        this.callbackEvent();
                    }
                };
            }

            this.worker.postMessage({
                type: 'start',
                payload: { finalTime: this.totalTime, elapsedTime: this.elapsedTime },
            });
        },
        stopWorker() {
            if (this.worker) {
                this.worker.postMessage({ type: 'stop' });
                this.worker.terminate();
                this.worker = null;
            }
        },
        updateWorker(payload) {
            if (this.worker) {
                this.worker.postMessage({ type: 'update', payload });
            }
        },
    },
};
</script>